import { Form } from "@unform/web";
import { useEffect, useState } from "react";

import { HTTPErrorCallback } from "Helpers/Error";

import { UseGlobalLoading } from "Hooks/UseGlobalLoading";
import UseNotification from "Hooks/UseNotification";
import UseServices from "Hooks/UseServices";

import Button from "Components/Global/Buttons";
import Select from "Components/Global/Form/Select";

import * as S from "./styles";

const ConversationModal = ({
  projectId,
  setOpenModal,
  getProjectConversations,
}) => {
  const { api } = UseServices();
  const { showNotification } = UseNotification();
  const { loading, setLoading } = UseGlobalLoading();

  const [sponsorOptions, setSponsorOptions] = useState([]);
  const [selectedSponsor, setSelectedSponsor] = useState(null);

  // EQUIPE SEVENX (22/09/2026): proponente e patrocinador passam a poder abrir
  // conversa com um administrador. Vem em lista PRÓPRIA do backend.
  const [adminOptions, setAdminOptions] = useState([]);
  const [selectedAdmin, setSelectedAdmin] = useState(null);

  const [isSponsor, setIsSponsor] = useState(false);

  useEffect(() => getAvailableConversations(), []);

  const getAvailableConversations = () => {
    api
      .post("/projects/available-conversations", { project_id: projectId })
      .then((response) => {
        const users = response.data?.available_users;

        const toOption = (user) => ({
          label: user?.full_name,
          value: user.id,
        });

        setSponsorOptions((users || []).map(toOption));
        setAdminOptions((response.data?.admin_users || []).map(toOption));

        setIsSponsor(response.data?.is_sponsor);
      })
      .catch((error) => {
        return HTTPErrorCallback(error);
      });
  };

  // Os dois campos são excludentes: a conversa tem UM destinatário.
  const target = selectedSponsor || selectedAdmin;

  const pickSponsor = (option) => {
    setSelectedSponsor(option);
    if (option) setSelectedAdmin(null);
  };

  const pickAdmin = (option) => {
    setSelectedAdmin(option);
    if (option) setSelectedSponsor(null);
  };

  const submit = async (data) => {
    if (!target) return;

    setLoading(true);

    try {
      await api.post("/projects/conversations/create", {
        ...data,
        project_id: projectId,
        target_user_id: target.value,
      });

      setLoading(false);

      showNotification("success", "Sucesso!", "Conversa criada com sucesso!");

      setOpenModal(null);
      getProjectConversations(projectId);
    } catch (error) {
      setLoading(false);
      return HTTPErrorCallback(error);
    }
  };

  return (
    <S.Wrapper>
      <S.Modal>
        <Form onSubmit={submit}>
          <S.Section>
            <span>Criar Conversa</span>
            <hr />
          </S.Section>

          {/* A regra dos dois campos, ANTES de o usuário esbarrar nela: sem a
              frase, ele só descobre que são excludentes ao marcar o segundo e
              ver o primeiro se apagar sozinho. */}
          <S.Hint>
            Escolha <strong>apenas um</strong> dos campos abaixo — a conversa
            tem um único destinatário. Ao selecionar um, o outro é limpo
            automaticamente.
          </S.Hint>

          <Select
            name="sponsor_id"
            label={isSponsor ? "Proponente" : "Patrocinador"}
            options={sponsorOptions}
            placeholder={`Selecione um ${isSponsor ? "Proponente" : "Patrocinador"}`}
            value={selectedSponsor}
            onChange={pickSponsor}
            maxWidth="100%"
            isClearable
          />

          {/* A Equipe Sevenx é sempre uma opção: mesmo sem patrocinador (ou
              proponente) disponível, há com quem falar. */}
          <Select
            name="admin_id"
            label="Equipe Sevenx"
            options={adminOptions}
            placeholder="Selecione alguém da Equipe Sevenx"
            value={selectedAdmin}
            onChange={pickAdmin}
            maxWidth="100%"
            isClearable
          />

          {/* Dizer só "nenhum usuário disponível" leva a crer que a tela
              quebrou. Na prática o motivo quase sempre é este: já existe
              conversa aberta com todo mundo que poderia ser oferecido. */}
          {!sponsorOptions.length && !adminOptions.length ? (
            <span>
              Nenhum usuário disponível — você já tem conversa aberta com todos.
              Volte e escolha uma das conversas existentes.
            </span>
          ) : null}

          <S.Buttons>
            <Button
              type="button"
              outline
              text="Fechar"
              disabled={loading}
              onClick={() => setOpenModal(null)}
            />

            <Button type="submit" text="Criar" disabled={loading || !target} />
          </S.Buttons>
        </Form>
      </S.Modal>
    </S.Wrapper>
  );
};

export default ConversationModal;
